import React, { useEffect, useState, useRef } from 'react';
import { motion, useScroll, useTransform, useSpring } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowRightIcon } from 'lucide-react';
import { FloatingCode } from './FloatingCode';
interface VideoHeroProps {
  title?: string;
  subtitle?: string;
  primaryCta?: {
    text: string;
    href: string;
  };
  secondaryCta?: {
    text: string;
    href: string;
  };
}
const terminalLines = [{
  text: '$ npm run deploy --production',
  color: 'text-[#E8781A]/70'
}, {
  text: '▸ Building optimized bundle...',
  color: 'text-white/30'
}, {
  text: '▸ Compiling TypeScript (3.2s)',
  color: 'text-white/30'
}, {
  text: '▸ Running tests — 142 passed',
  color: 'text-green-500/50'
}, {
  text: '▸ Optimizing assets (847kb → 312kb)',
  color: 'text-white/30'
}, {
  text: '▸ Deploying to eu-west-1...',
  color: 'text-[#a855f7]/50'
}, {
  text: '✓ Deploy successful',
  color: 'text-green-500/60'
}, {
  text: '',
  color: ''
}, {
  text: '$ docker ps --format "table"',
  color: 'text-[#E8781A]/70'
}, {
  text: '  CONTAINER   STATUS    CPU   MEM',
  color: 'text-white/20'
}, {
  text: '  api-prod    running   12%   256MB',
  color: 'text-white/30'
}, {
  text: '  web-prod    running    8%   128MB',
  color: 'text-white/30'
}, {
  text: '  db-primary  running    4%   512MB',
  color: 'text-green-500/40'
}, {
  text: '',
  color: ''
}, {
  text: '$ git log --oneline -5',
  color: 'text-[#E8781A]/70'
}, {
  text: '  a3f2c1d feat: add real-time sync',
  color: 'text-white/30'
}, {
  text: '  b7e4a09 fix: auth token refresh',
  color: 'text-white/30'
}, {
  text: '  c1d8f32 perf: optimize queries',
  color: 'text-[#a855f7]/40'
}];
const systemMetrics = [{
  label: 'CPU',
  value: 23,
  max: 100
}, {
  label: 'MEM',
  value: 67,
  max: 100
}, {
  label: 'NET',
  value: 42,
  max: 100
}, {
  label: 'I/O',
  value: 15,
  max: 100
}];
export function VideoHero({
  title = 'Digitale Lösungen, die Unternehmen voranbringen.',
  subtitle = 'Wir sind Ihr strategischer Partner für Softwareentwicklung, Markendesign und digitale Transformation im DACH-Raum.',
  primaryCta = {
    text: 'Projekt besprechen',
    href: '/kontakt'
  },
  secondaryCta = {
    text: 'Leistungen entdecken',
    href: '/leistungen'
  }
}: VideoHeroProps) {
  const words = title.split(' ');
  const [revealed, setRevealed] = useState(false);
  const [visibleLines, setVisibleLines] = useState(0);
  const sectionRef = useRef<HTMLElement>(null);
  const {
    scrollYProgress
  } = useScroll({
    target: sectionRef,
    offset: ['start start', 'end start']
  });
  const smoothProgress = useSpring(scrollYProgress, {
    stiffness: 100,
    damping: 30,
    restDelta: 0.001
  });
  // Gate panels — open across the pinned scroll phase
  const gateLeftX = useTransform(smoothProgress, [0, 0.5], ['0%', '-105%']);
  const gateRightX = useTransform(smoothProgress, [0, 0.5], ['0%', '105%']);
  const gateOpacity = useTransform(smoothProgress, [0.4, 0.55], [1, 0]);
  const seamOpacity = useTransform(smoothProgress, [0, 0.1, 0.4], [0.6, 1, 0]);
  // All layers stay completely static — no parallax movement
  // The sticky container handles pinning; content doesn't move at all
  // Scroll indicator — fades as user starts scrolling
  const scrollIndicatorOpacity = useTransform(smoothProgress, [0, 0.08], [1, 0]);
  useEffect(() => {
    const timer = setTimeout(() => setRevealed(true), 100);
    return () => clearTimeout(timer);
  }, []);
  // Terminal typing animation
  useEffect(() => {
    if (!revealed) return;
    const interval = setInterval(() => {
      setVisibleLines(prev => {
        if (prev >= terminalLines.length) {
          clearInterval(interval);
          return prev;
        }
        return prev + 1;
      });
    }, 300);
    return () => clearInterval(interval);
  }, [revealed]);
  return <section ref={sectionRef} className="relative h-[200vh] bg-transparent -mt-20" data-id="element-177">
      <div className="sticky top-0 h-screen w-full overflow-hidden" data-id="element-178">
        {/* Full-page mask reveal overlay (Entrance) */}
        <motion.div className="absolute inset-0 z-[60] bg-[#09090B] pointer-events-none" initial={{
        clipPath: 'inset(0% 0% 0% 0%)'
      }} animate={{
        clipPath: 'inset(0% 0% 100% 0%)'
      }} transition={{
        duration: 1.4,
        delay: 0.2,
        ease: [0.76, 0, 0.24, 1]
      }} data-id="element-179" />

        {/* 3D perspective entrance wrapper */}
        <motion.div className="absolute inset-0 w-full h-full" initial={{
        rotateX: 8,
        scale: 0.92,
        opacity: 0
      }} animate={{
        rotateX: 0,
        scale: 1,
        opacity: 1
      }} transition={{
        duration: 1.6,
        delay: 0.3,
        ease: [0.25, 1, 0.5, 1]
      }} style={{
        transformOrigin: 'center bottom',
        transformStyle: 'preserve-3d',
        perspective: '1400px'
      }} data-id="element-180">
          {/* ===== Gate Panels (spaceship blast doors) ===== */}
          {/* Left Gate */}
          <motion.div className="absolute inset-y-0 left-0 w-1/2 z-[2] pointer-events-none" style={{
          x: gateLeftX,
          opacity: gateOpacity
        }} data-id="element-181">
            <div className="relative w-full h-full bg-[#09090B] overflow-hidden" data-id="element-182">
              <div className="absolute inset-0 opacity-[0.08]" style={{
              backgroundImage: 'repeating-linear-gradient(0deg, transparent, transparent 3px, rgba(232,120,26,0.15) 3px, rgba(232,120,26,0.15) 4px)',
              backgroundSize: '100% 8px'
            }} data-id="element-183" />
              <div className="absolute top-1/4 right-8 w-24 h-px bg-gradient-to-l from-[#E8781A]/30 to-transparent" data-id="element-184" />
              <div className="absolute top-1/4 right-8 mt-3 w-16 h-px bg-gradient-to-l from-[#E8781A]/20 to-transparent" data-id="element-185" />
              <div className="absolute top-1/3 right-8 w-32 h-px bg-gradient-to-l from-[#a855f7]/20 to-transparent" data-id="element-186" />
              <div className="absolute bottom-1/4 right-8 w-20 h-px bg-gradient-to-l from-[#E8781A]/25 to-transparent" data-id="element-187" />
              <div className="absolute bottom-1/3 right-8 w-28 h-px bg-gradient-to-l from-[#a855f7]/15 to-transparent" data-id="element-188" />
              <div className="absolute top-[20%] right-6 flex flex-col gap-3" data-id="element-189">
                <div className="w-1.5 h-1.5 rounded-full bg-[#E8781A]/60 shadow-[0_0_6px_rgba(232,120,26,0.4)]" data-id="element-190" />
                <div className="w-1.5 h-1.5 rounded-full bg-[#a855f7]/40" data-id="element-191" />
                <div className="w-1.5 h-1.5 rounded-full bg-[#E8781A]/30" data-id="element-192" />
              </div>
              <div className="absolute top-0 right-0 w-px h-full bg-gradient-to-b from-transparent via-[#E8781A]/20 to-transparent" data-id="element-193" />
              <div className="absolute top-8 right-4 w-4 h-4 border-t border-r border-[#E8781A]/20" data-id="element-194" />
              <div className="absolute bottom-8 right-4 w-4 h-4 border-b border-r border-[#E8781A]/20" data-id="element-195" />
            </div>
          </motion.div>

          {/* Right Gate — with animated tech content */}
          <motion.div className="absolute inset-y-0 right-0 w-1/2 z-[2] pointer-events-none" style={{
          x: gateRightX,
          opacity: gateOpacity
        }} data-id="element-196">
            <div className="relative w-full h-full bg-[#09090B] overflow-hidden" data-id="element-197">
              {/* Scan lines */}
              <div className="absolute inset-0 opacity-[0.08]" style={{
              backgroundImage: 'repeating-linear-gradient(0deg, transparent, transparent 3px, rgba(168,85,247,0.15) 3px, rgba(168,85,247,0.15) 4px)',
              backgroundSize: '100% 8px'
            }} data-id="element-198" />

              {/* Left edge (seam side) */}
              <div className="absolute top-0 left-0 w-px h-full bg-gradient-to-b from-transparent via-[#a855f7]/20 to-transparent" data-id="element-199" />
              <div className="absolute top-8 left-4 w-4 h-4 border-t border-l border-[#a855f7]/20" data-id="element-200" />
              <div className="absolute bottom-8 left-4 w-4 h-4 border-b border-l border-[#a855f7]/20" data-id="element-201" />

              {/* ===== ANIMATED TECH CONTENT ===== */}

              {/* Terminal Output */}
              <div className="absolute top-[12%] left-10 right-6 font-mono text-[10px] leading-[1.6] select-none" data-id="element-202">
                <div className="flex items-center gap-2 mb-3" data-id="element-203">
                  <div className="w-2 h-2 rounded-full bg-red-500/50" data-id="element-204" />
                  <div className="w-2 h-2 rounded-full bg-yellow-500/50" data-id="element-205" />
                  <div className="w-2 h-2 rounded-full bg-green-500/50" data-id="element-206" />
                  <span className="text-white/20 ml-2 text-[9px]" data-id="element-207">
                    terminal — zsh
                  </span>
                </div>
                {terminalLines.slice(0, visibleLines).map((line, i) => <div key={i} className={`${line.color} whitespace-nowrap overflow-hidden`} data-id="element-208">
                    {line.text || '\u00A0'}
                  </div>)}
                {visibleLines < terminalLines.length && <motion.span className="inline-block w-[6px] h-[12px] bg-[#E8781A]/60" animate={{
                opacity: [1, 0]
              }} transition={{
                duration: 0.6,
                repeat: Infinity
              }} data-id="element-209" />}
              </div>

              {/* System Metrics */}
              <div className="absolute top-[55%] left-10 right-6 font-mono text-[9px] select-none" data-id="element-210">
                <div className="text-white/25 uppercase tracking-widest mb-3 text-[8px]" data-id="element-211">
                  System Monitor
                </div>
                {systemMetrics.map((metric, i) => <div key={metric.label} className="flex items-center gap-2 mb-2" data-id="element-212">
                    <span className="text-white/30 w-6" data-id="element-213">{metric.label}</span>
                    <div className="flex-1 h-[3px] bg-white/5 rounded-full overflow-hidden" data-id="element-214">
                      <motion.div className="h-full rounded-full" style={{
                    background: i % 2 === 0 ? 'linear-gradient(90deg, #E8781A, #a855f7)' : 'linear-gradient(90deg, #a855f7, #E8781A)'
                  }} initial={{
                    width: '0%'
                  }} animate={{
                    width: `${metric.value}%`
                  }} transition={{
                    duration: 1.5,
                    delay: 2 + i * 0.3,
                    ease: 'easeOut'
                  }} data-id="element-215" />
                    </div>
                    <motion.span className="text-white/25 w-8 text-right" initial={{
                  opacity: 0
                }} animate={{
                  opacity: 1
                }} transition={{
                  delay: 2.5 + i * 0.3
                }} data-id="element-216">
                      {metric.value}%
                    </motion.span>
                  </div>)}
              </div>

              {/* Hex Data Stream */}
              <div className="absolute bottom-[18%] left-10 right-6 font-mono text-[8px] text-white/10 leading-[1.8] select-none overflow-hidden" data-id="element-217">
                <div className="text-white/20 uppercase tracking-widest mb-2 text-[8px]" data-id="element-218">
                  Data Stream
                </div>
                <motion.div animate={{
                y: [0, -60]
              }} transition={{
                duration: 8,
                repeat: Infinity,
                ease: 'linear'
              }} data-id="element-219">
                  {Array.from({
                  length: 12
                }).map((_, i) => <div key={i} className="whitespace-nowrap" data-id="element-220">
                      {Array.from({
                    length: 8
                  }).map((_, j) => {
                    const hex = ((i * 8 + j) * 0xa3f + 0x1b7).toString(16).toUpperCase().padStart(4, '0');
                    return <span key={j} className={j % 3 === 0 ? 'text-[#E8781A]/15' : j % 3 === 1 ? 'text-[#a855f7]/12' : 'text-white/8'} data-id="element-221">
                            {hex}{' '}
                          </span>;
                  })}
                    </div>)}
                </motion.div>
              </div>

              {/* Network Activity Dots */}
              <div className="absolute top-[45%] left-10 flex items-center gap-1.5" data-id="element-222">
                {Array.from({
                length: 8
              }).map((_, i) => <motion.div key={i} className="w-1 h-1 rounded-full bg-[#a855f7]/40" animate={{
                opacity: [0.2, 0.8, 0.2],
                scale: [0.8, 1.2, 0.8]
              }} transition={{
                duration: 1.5,
                delay: i * 0.15,
                repeat: Infinity,
                ease: 'easeInOut'
              }} data-id="element-223" />)}
                <span className="text-[8px] font-mono text-white/20 ml-2" data-id="element-224">
                  ● LIVE
                </span>
              </div>

              {/* Status indicators (right side) */}
              <div className="absolute top-[20%] left-6 flex flex-col gap-3" data-id="element-225">
                <div className="w-1.5 h-1.5 rounded-full bg-[#a855f7]/60 shadow-[0_0_6px_rgba(168,85,247,0.4)]" data-id="element-226" />
                <div className="w-1.5 h-1.5 rounded-full bg-[#E8781A]/40" data-id="element-227" />
                <div className="w-1.5 h-1.5 rounded-full bg-[#a855f7]/30" data-id="element-228" />
              </div>
            </div>
          </motion.div>

          {/* Center seam glow */}
          <motion.div className="absolute inset-y-0 left-1/2 -translate-x-1/2 w-[2px] z-[3] pointer-events-none" style={{
          opacity: seamOpacity
        }} data-id="element-229">
            <div className="w-full h-full bg-gradient-to-b from-transparent via-[#E8781A]/60 to-transparent shadow-[0_0_15px_rgba(232,120,26,0.4),0_0_30px_rgba(232,120,26,0.2)]" data-id="element-230" />
          </motion.div>

          {/* ===== Layer 1: Ambient effects — static, no parallax ===== */}
          <div className="absolute inset-0 pointer-events-none z-[4]" data-id="element-231">
            <div className="absolute inset-0 overflow-hidden" data-id="element-232">
              {/* Desktop: animated blobs, Mobile: static smaller blobs */}
              <motion.div className="absolute rounded-full hidden md:block" style={{
              width: 700,
              height: 700,
              filter: 'blur(64px)',
              background: 'radial-gradient(circle, rgba(232,120,26,0.15) 0%, transparent 70%)',
              top: '-15%',
              left: '-10%'
            }} animate={{
              x: [0, 80, 40, 0],
              y: [0, 60, 120, 0]
            }} transition={{
              duration: 20,
              repeat: Infinity,
              ease: 'linear'
            }} data-id="element-233" />
              <div className="absolute rounded-full md:hidden" style={{
              width: 300,
              height: 300,
              filter: 'blur(40px)',
              background: 'radial-gradient(circle, rgba(232,120,26,0.12) 0%, transparent 70%)',
              top: '-10%',
              left: '-5%'
            }} data-id="element-234" />
              <motion.div className="absolute rounded-full hidden md:block" style={{
              width: 600,
              height: 600,
              filter: 'blur(64px)',
              background: 'radial-gradient(circle, rgba(168,85,247,0.12) 0%, transparent 70%)',
              bottom: '-10%',
              right: '-5%'
            }} animate={{
              x: [0, -60, -30, 0],
              y: [0, -80, -40, 0]
            }} transition={{
              duration: 25,
              repeat: Infinity,
              ease: 'linear'
            }} data-id="element-235" />
              <div className="absolute rounded-full md:hidden" style={{
              width: 250,
              height: 250,
              filter: 'blur(40px)',
              background: 'radial-gradient(circle, rgba(168,85,247,0.1) 0%, transparent 70%)',
              bottom: '-5%',
              right: '-3%'
            }} data-id="element-236" />
            </div>
            <div className="absolute inset-0 opacity-[0.03] hidden md:block" style={{
            backgroundImage: `
                linear-gradient(rgba(255,255,255,0.05) 1px, transparent 1px),
                linear-gradient(90deg, rgba(255,255,255,0.05) 1px, transparent 1px)
              `,
            backgroundSize: '60px 60px'
          }} data-id="element-237" />
          </div>

          {/* ===== Layer 2: Main Content — static, no parallax ===== */}
          <div className="absolute inset-0 flex items-center z-10" data-id="element-238">
            <div className="max-w-7xl mx-auto px-6 md:px-8 lg:px-12 w-full" data-id="element-239">
              <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-extrabold tracking-tightest max-w-4xl leading-[1.1]" data-id="element-240">
                {words.map((word, index) => <span key={index} className="inline-block overflow-hidden mr-[0.25em] pb-2" data-id="element-241">
                    <motion.span className="inline-block gradient-text" initial={{
                  y: '110%',
                  rotateX: 40
                }} animate={{
                  y: '0%',
                  rotateX: 0
                }} transition={{
                  duration: 0.8,
                  delay: 0.6 + index * 0.1,
                  ease: [0.25, 1, 0.5, 1]
                }} style={{
                  transformOrigin: 'bottom center'
                }} data-id="element-242">
                      {word}
                    </motion.span>
                  </span>)}
              </h1>

              <div className="overflow-hidden mt-8" data-id="element-243">
                <motion.p className="text-lg md:text-xl text-white/50 max-w-2xl leading-relaxed" initial={{
                y: '100%',
                opacity: 0
              }} animate={{
                y: '0%',
                opacity: 1
              }} transition={{
                duration: 0.7,
                delay: 1.3,
                ease: [0.25, 1, 0.5, 1]
              }} data-id="element-244">
                  {subtitle}
                </motion.p>
              </div>

              <motion.div className="mt-6 h-px bg-gradient-to-r from-[#E8781A] to-[#a855f7]" initial={{
              width: 0,
              opacity: 0
            }} animate={{
              width: 120,
              opacity: 1
            }} transition={{
              duration: 0.8,
              delay: 1.6,
              ease: 'easeOut'
            }} data-id="element-245" />

              <div className="overflow-hidden mt-10" data-id="element-246">
                <motion.div className="flex flex-col sm:flex-row gap-4" initial={{
                y: '120%',
                rotateX: 20
              }} animate={{
                y: '0%',
                rotateX: 0
              }} transition={{
                duration: 0.7,
                delay: 1.8,
                ease: [0.25, 1, 0.5, 1]
              }} style={{
                transformOrigin: 'bottom center'
              }} data-id="element-247">
                  <Link to={primaryCta.href} className="inline-flex items-center justify-center px-8 py-4 bg-gradient-to-r from-[#E8781A] to-[#a855f7] text-white font-medium rounded-lg hover:opacity-90 transition-opacity shadow-lg shadow-[#E8781A]/20" data-id="element-248">
                    {primaryCta.text}
                    <ArrowRightIcon className="ml-2 w-4 h-4" data-id="element-249" />
                  </Link>
                  <Link to={secondaryCta.href} className="inline-flex items-center justify-center px-8 py-4 border border-white/20 text-white font-medium rounded-lg hover:bg-white/5 transition-colors" data-id="element-250">
                    {secondaryCta.text}
                  </Link>
                </motion.div>
              </div>
            </div>
          </div>

          {/* ===== Layer 3: Foreground decorations — desktop only ===== */}
          <div className="absolute inset-0 pointer-events-none z-20 hidden md:block" data-id="element-251">
            <FloatingCode data-id="element-252" />
            <div className="absolute top-1/4 right-[10%] text-white/10 font-mono text-4xl font-bold" data-id="element-253">
              &lt;/&gt;
            </div>
            <div className="absolute bottom-1/3 left-[5%] text-white/10 font-mono text-5xl font-bold" data-id="element-254">
              {'{ }'}
            </div>
            <div className="absolute top-1/3 left-[15%] text-[#E8781A]/10 font-mono text-3xl font-bold" data-id="element-255">
              =&gt;
            </div>
          </div>

          {/* Film grain overlay — desktop only */}
          <div className="absolute inset-0 pointer-events-none opacity-[0.03] z-30 hidden md:block" style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)'/%3E%3C/svg%3E")`,
          backgroundRepeat: 'repeat',
          backgroundSize: '128px 128px'
        }} data-id="element-256" />

          {/* Scroll indicator */}
          <motion.div className="absolute bottom-12 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 z-40" initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} transition={{
          delay: 2.5,
          duration: 0.6
        }} style={{
          opacity: scrollIndicatorOpacity
        }} data-id="element-257">
            <span className="text-[10px] uppercase tracking-[0.2em] text-white/30 font-medium" data-id="element-258">
              Scroll
            </span>
            <motion.div className="w-px h-8 bg-gradient-to-b from-white/40 to-transparent" animate={{
            scaleY: [0, 1, 0]
          }} transition={{
            duration: 2,
            repeat: Infinity,
            ease: 'easeInOut'
          }} style={{
            transformOrigin: 'top'
          }} data-id="element-259" />
          </motion.div>
        </motion.div>
      </div>
    </section>;
}